import Add from '@mui/icons-material/Add';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import Star from '@mui/icons-material/Star';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import StarBorder from '@mui/icons-material/StarBorder';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AISolutionId,
  IAISolution,
  IAllAgents,
  IEnhancedAISolution,
  IMutationAISolution,
} from '../../common/types/common';
import { useGraphQLMutation } from '../../customHooks/useGraphQL';
import { Alert, Snackbar } from '@mui/material';
import Loader from '../../common/components/Loader.tsx';
import Modal from '../../common/components/Modal/Modal.tsx';
import { getBenefitLabelNew, getBenefitTitle } from '../../common/utils';
import { API_BASE, ENVIRONMENT_TYPE } from '../../config/env';
import { httpConstants } from '../../constants';
import { UPDATE_FAVORITE_AI_SOLUTIONS } from '../../graphql/mutations/common';
import { httpService } from '../../utility/httpService';
import SolutionEditModal from './SolutionEditModal.tsx';
import { getEnterpriseTechnology } from '../../services/commonService.tsx';
import ConfirmationModal from '../../common/components/ConfirmationModal.tsx';
import {
  fetchAISolutionByID,
  fetchAISolutionWithAgentEnablement,
} from '../../utility/aiOpportunityUtils';
import TopBenefits from './TopBenefits.tsx';
import { useNavigate } from 'react-router-dom';

interface ExtraAgentData {
  agent_name: string;
  implementation_type: string;
  justification: string;
}

interface CardData {
  capability_name: string;
  description: string;
}

// Helper function to check if sync is needed based on timestamp comparison
const checkIfSyncNeeded = async (enterpriseTechResponse: any, agent: any): Promise<boolean> => {
  try {
    // Check if agent timestamp exists
    if (!agent?.updated_at) {
      return true;
    }
    // Check if enterprise tech timestamp exists
    if (!enterpriseTechResponse?.updated_at) {
      return false;
    }

    // Parse timestamps - both come without timezone info, treat as UTC

    const agentTimestamp = agent.updated_at.endsWith('Z')
      ? agent.updated_at
      : agent.updated_at + 'Z';

    const agentDate = new Date(agentTimestamp);
    const agentUpdatedAt = agentDate.getTime();

    // enterpriseTechResponse.updated_at
    const techTimestamp = enterpriseTechResponse.updated_at.endsWith('Z')
      ? enterpriseTechResponse.updated_at
      : enterpriseTechResponse.updated_at + 'Z';
    const techDate = new Date(techTimestamp);
    const techUpdatedAt = techDate.getTime();

    if (techUpdatedAt > agentUpdatedAt) {
      console.log('🔄 Sync needed: Enterprise Technology is newer than Agent');
      return true;
    }
    return false;
  } catch (error) {
    console.error('Error comparing timestamps:', error);
    return false;
  }
};

const makeAgentSyncAPICall = async (
  aiSolutionData: any,
  enterpriseTechResponse: any,
  additionalPayload?: any
) => {
  try {
    const { company_name, description, industry_data } = enterpriseTechResponse;
    const {
      id,
      roi,
      impact,
      benefits,
      complexity,
      data_sources,
      key_requirements,
      opportunity_name,
    } = aiSolutionData || {};

    const basePayload = {
      company_info: {
        company_name,
        description,
      },
      technology: industry_data,
      aiSolution: {
        solution_id: id,
        roi,
        impact,
        benefits,
        complexity,
        data_sources,
        key_requirements,
        opportunity_name,
        description: aiSolutionData?.description || '',
      },
    };

    // Merge with additional payload if provided
    const payload = additionalPayload
      ? { ...basePayload, ...additionalPayload }
      : {
          ...basePayload,
          llm_agent: aiSolutionData?.llm_agent,
          solution_enabler: aiSolutionData?.solution_enabler,
          diagram_id: aiSolutionData?.diagram_id,
          organization_id: localStorage.getItem('current_organization') || '',
          type: ENVIRONMENT_TYPE,
        };

    const AGENT_URL_1 =
      'https://orchestrator.app.zbrain.ai/api/v1/webhooks/qtD4OqGQNyRHYPpizv6Yu/sync?tenantId=6764029052789d00255f17a1';

    const AGENT_URL_2 =
      'https://orchestrator.zbrain.ai/api/v1/webhooks/EaqsB7pt8hBFF82dB8nnX/sync?tenantId=6764029052789d00255f17a1';

    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer za-U2FsdGVkX1/ttZ/pUsCjolOeYbHP4uWJKYu0O860+8yYE6ebW6DvrABLSug5Qkw4DIVv+2HmxvP3kq9t+WtL3P4Wt1KaaAc43piiuclp840Ek1C9ibGtQGD44w9fTXuK`,
    };

    const response = await httpService<any>(
      httpConstants.METHOD_TYPE.POST,
      AGENT_URL_1,
      payload,
      headers,
      false
    );

    // Map first API response to frontend state
    // This will be handled by the caller

    // Call second API
    const secondResponse = await httpService<any>(
      httpConstants.METHOD_TYPE.POST,
      AGENT_URL_2,
      payload,
      headers,
      false
    );

    // Append/combine responses from both APIs
    const combinedResponse = {
      ...response,
      ...secondResponse,
      // Properly merge ai_enablement objects
      ai_enablement: {
        ...(typeof response.ai_enablement === 'object' && response.ai_enablement
          ? response.ai_enablement
          : {}),
        ...(typeof secondResponse.ai_enablement === 'object' && secondResponse.ai_enablement
          ? secondResponse.ai_enablement
          : {}),
      },
      // Combine agent_classification arrays
      agent_classification: [
        ...(Array.isArray(response.agent_classification) ? response.agent_classification : []),
        ...(Array.isArray(secondResponse.agent_classification)
          ? secondResponse.agent_classification
          : []),
      ],
      // Combine application_in_use arrays
      application_in_use: [
        ...(Array.isArray(response.application_in_use) ? response.application_in_use : []),
        ...(Array.isArray(secondResponse.application_in_use)
          ? secondResponse.application_in_use
          : []),
      ],
      // Add other fields as needed
    };

    return combinedResponse;
  } catch (error) {
    console.error('Error making agent sync API call:', error);
    throw error;
  }
};

const EnablementCard: React.FC<{
  title: string;
  content: string;
  bgcolor: string;
  padding?: string;
}> = ({ title, content, bgcolor, padding = '16px' }) => {
  return (
    <Box
      sx={{
        p: {
          padding,
        },
        borderRadius: '8px',
        bgcolor,
      }}
    >
      <Typography
        sx={{
          fontFamily: 'Poppins',
          fontWeight: 600,
          fontSize: '14px',
          lineHeight: '20px',
          letterSpacing: '-0.01em',
          mb: 1,
          color: '#111827',
        }}
      >
        {title}
      </Typography>
      <Typography
        sx={{
          fontFamily: 'Poppins',
          fontWeight: 400,
          fontSize: '15px',
          lineHeight: '20px',
          letterSpacing: '-0.01em',
          color: '#000000',
        }}
      >
        {content}
      </Typography>
    </Box>
  );
};
const getApprovalStatusConfig = (status?: string | null) => {
  if (!status) return { text: '', show: false, color: '', bgColor: '' };

  const normalized = String(status).trim().toLowerCase();

  if (normalized.includes('pending')) {
    return {
      text: 'Approval pending',
      color: '#FF0505',
      bgColor: '#FFF5F2',
      show: true,
    };
  }

  if (normalized.includes('approved')) {
    return {
      text: 'Approved',
      color: '#11953E',
      bgColor: '#DBFFE8',
      show: true,
    };
  }

  if (normalized.includes('solution')) {
    return {
      text: 'Solutioning',
      color: '#11953E',
      bgColor: '#DBFFE8',
      show: true,
    };
  }

  if (normalized.includes('build')) {
    return {
      text: 'Build',
      color: '#11953E',
      bgColor: '#DBFFE8',
      show: true,
    };
  }

  return { text: '', show: false, color: '', bgColor: '' };
};
export type EditType =
  | 'overview'
  | 'roi'
  | 'complexity'
  | 'key_requirements'
  | 'data_requirements'
  | 'benefits'
  | 'agent_name'
  | 'add_key_requirements'
  | 'add_data_requirements'
  | 'name'
  | 'impact'
  | 'embedded'
  | 'extended'
  | 'custom'
  | 'risk';

type ViewSolutionDetailsProps = {
  selectedSolutionId: AISolutionId;
  selectedSolutionFromLibrary: any;
  enterpriseContextApplied?: boolean;
  setEnterpriseContextApplied?: (value: boolean) => void;
  aiSolutionData?: any;
  setAiSolutionData?: any;
  diagramId?: string;
  // setSelectedSolutionFromLibrary: (value: any) => void;
  handleToggleVault: () => void;
  handleUpdateSolution: (data: any) => void;
  handleSave: (data: any) => void;
  isEditable?: boolean;
  isProcessDiagramActive?: boolean;
  onApproval?: (updatedSolution: any) => void;
  handleRemoveClick?: () => void;
  closeDrawer?: () => void;
  isIdeation?: boolean;
  processType?: 'custom' | 'standard' | string;
};

const ViewSolutionDetails: React.FC<ViewSolutionDetailsProps> = ({
  selectedSolutionId,
  selectedSolutionFromLibrary,
  enterpriseContextApplied = false,
  setEnterpriseContextApplied = () => {},
  aiSolutionData,
  setAiSolutionData,
  diagramId,
  // setSelectedSolutionFromLibrary,
  handleToggleVault,
  handleUpdateSolution,
  handleSave,
  isEditable = true,
  isProcessDiagramActive = false,
  onApproval,
  handleRemoveClick,
  closeDrawer,
  isIdeation = false,
  processType = 'standard',
}) => {
  const [, setSolution] = useState<IEnhancedAISolution | null>(null);
  const [, setSource] = useState<string[]>([]);
  const [isFavorite, setIsFavorite] = useState<boolean>(false);
  const [, setDataSources] = useState<string>('');
  const [, setImplementationConsiderations] = useState<string>('');
  const [showApplySuccessSnackbar, setShowApplySuccessSnackbar] = useState(false);
  const [activePiece, setActivePiece] = useState<string>('information');
  const [agentLlmResponse, setAgentLlmResponse] = useState<ExtraAgentData[]>([]);
  const [isLoadingAgentLlmResponse, setIsLoadingAgentLlmResponse] = useState<boolean>(false);

  const currentSolutionIdRef = useRef<AISolutionId | null>(null);
  const hasLoadedImplementationRef = useRef<boolean>(false);

  const currentOrg = useMemo(() => localStorage.getItem('current_organization') || 'Empty', []);
  const [showFavoriteModal, setShowFavoriteModal] = useState(false);
  const [queryLoading, setQueryLoading] = useState(false);
  const [queryError, setQueryError] = useState<any>(null);
  const [llmResponseLoading, setLlmResponseLoading] = useState<boolean>(false);
  const [llmResponseData, setLlmResponseData] = useState<any>([]);
  const [isLoadingImplementation] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const isMobile = useMediaQuery('(max-width: 600px)');
  const [updatedSolutionData, setUpdatedSolutionData] = useState<any>(null);
  const [editItem, setEditItem] = useState<any>(null);
  const [openEditModal, setOpenEditModal] = useState<boolean>(false);
  const [editType, setEditType] = useState<EditType | string>('');
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState<boolean>(false);
  const [updatedAiSolution, setUpdatedAiSolution] = useState<any>(null);

  const [enablementExpanded, setEnablementExpanded] = useState<Record<string, boolean>>({
    embedded: true,
    extended: true,
    custom: true,
  });

  const navigate = useNavigate();

  const [agentClickCount, setAgentClickCount] = useState<number>(0);
  const [lastClickTime, setLastClickTime] = useState<number>(0);
  const [isManualSyncLoading, setIsManualSyncLoading] = useState<boolean>(false);
  const [, setIsLoadingEnterpriseTech] = useState<boolean>(false);
  const hasFetchedEnterpriseTechRef = useRef<boolean>(false);

  // Global cache to track recently synced solutions - persists across component instances
  if (!(window as any).solutionSyncCache) {
    (window as any).solutionSyncCache = new Map<string, number>();
  }
  const globalSyncedSolutions = (window as any).solutionSyncCache as Map<string, number>;

  // Global state for enterprise technology updates
  if (!(window as any).enterpriseTechLastUpdated) {
    (window as any).enterpriseTechLastUpdated = Date.now();
  }

  // Approval status state
  const [approvalStatus, setApprovalStatus] = useState<string | null>(null);
  const [isVaultActionLoading, setIsVaultActionLoading] = useState<boolean>(false);
  const [localVaultState, setLocalVaultState] = useState<boolean | null>(
    selectedSolutionFromLibrary?.vault ?? null
  );

  // Reset sync state when solution changes
  useEffect(() => {
    if (currentSolutionIdRef.current !== selectedSolutionId) {
      currentSolutionIdRef.current = selectedSolutionId;
      hasLoadedImplementationRef.current = false;
      hasFetchedEnterpriseTechRef.current = false;
      setDataSources('');
      setImplementationConsiderations('');
      setSource([]);

      // Reset admin sync state
      setAgentClickCount(0);
      setLastClickTime(0);
      setIsManualSyncLoading(false);

      // Clear global sync cache for this solution when switching solutions
      if (currentSolutionIdRef.current && (window as any).solutionSyncCache) {
        (window as any).solutionSyncCache.delete(currentSolutionIdRef.current);
      }
    }
  }, [selectedSolutionId]);

  const isIdeationEnablementLoading = isManualSyncLoading || isLoadingAgentLlmResponse;
  console.log('loading', isIdeationEnablementLoading);

  // Add IMPLEMENTATION_STYLES for Agents view
  const IMPLEMENTATION_STYLES: Record<string, { bg: string; color: string; icon: string }> = {
    'Client Specific': {
      bg: '#F1FFEE',
      color: '#27742A',
      icon: '/images/client_specific.svg',
    },
    Embedded: {
      bg: '#FFF7F3',
      color: '#9A5A37',
      icon: '/images/embedded_icon.svg',
    },
    Extended: {
      bg: '#FFF7DF',
      color: '#B18600',
      icon: '/images/extended_icon.svg',
    },
  };

  const renderInfoBox = ({
    title,
    titleColor,
    icon,
    text,
    bg,
    extraSx = {},
  }: {
    title: string;
    titleColor: string;
    icon: string;
    text: string;
    bg: string;
    extraSx?: object;
  }) => {
    return (
      <Box
        sx={{
          background: bg,
          borderRadius: '6px',
          padding: '12px',
          mt: '18px',
          width: { xs: '100%', sm: '50%' },
          minHeight: '126px',
          ...extraSx,
        }}
      >
        <Box sx={{ display: 'flex' }}>
          <img src={icon} alt="icon" />
          <Typography sx={{ fontWeight: 500, fontSize: '15px', color: titleColor, ml: '8px' }}>
            {title}
          </Typography>
        </Box>
        <Typography sx={{ fontSize: '15px', fontWeight: 400, mt: '10px' }}>{text}</Typography>
      </Box>
    );
  };

  // Add this function to handle approval
  const handleSendForApproval = async () => {
    try {
      // API call to send for approval
      const url = `${API_BASE}/api/v1/e2e_taxonomy/new_ai_opportunity/${selectedSolutionId}/`;
      await httpService(httpConstants.METHOD_TYPE.PATCH, url, {
        status: 'approval_pending',
        isAddOpportunityStatusHistory: true,
      });

      setApprovalStatus('pending');
      // Refetch solution data to get the actual approval status from API
      const updated = await fetchAISolutionByID({
        solutionId: String(selectedSolutionId),
        diagramId: diagramId || '',
        organizationId: currentOrg,
      });

      // Notify parent about updated solution so it can update its list / UI
      if (onApproval) {
        try {
          onApproval(updated);
        } catch (e) {
          console.warn('onApproval callback failed', e);
        }
      }

      // ShowToast({
      //   message: 'Solution sent for approval successfully',
      //   type: ToastTypeEnum.Success
      // });
    } catch (error) {
      console.error('Error sending for approval:', error);
      // ShowToast({
      //   message: 'Error sending solution for approval',
      //   type: ToastTypeEnum.Error
      // });
    }
  };

  const { mutate } = useGraphQLMutation<IMutationAISolution>(UPDATE_FAVORITE_AI_SOLUTIONS, {
    onCompleted: (data) => {
      console.log('Favorite updated successfully:', data);
    },
    onError: (error) => {
      console.error('Error updating favorite:', error);
    },
    refetchQueries: ['IMutationAISolution'],
  });

  // Helper to normalize/prepare solution data for UI
  const processSolutionData = useCallback((rawSolution: IAISolution): IEnhancedAISolution => {
    const allDataRequirements =
      rawSolution.aiOpportunitiesUsed?.flatMap((item) => item.data_requirements || []).join(', ') ||
      '';
    const allTopBenefits = rawSolution?.benefits
      ? Object.entries(rawSolution.benefits).map(([key, value]) => ({
          title: getBenefitTitle(key),
          label: getBenefitLabelNew(value as string),
        }))
      : [];
    const keyConsiderations = rawSolution?.keyConsiderations
      ? rawSolution?.keyConsiderations
      : rawSolution.potentialChallenges
          ?.split(',')
          .map((i) => i.trim())
          .filter((i) => i.length > 0) || [];
    const allAgents: IAllAgents | undefined = rawSolution.agentCollaboration?.length
      ? {
          description: rawSolution.agentCollaboration.map((c) => c.collaboration).join(' '),
          agents: rawSolution.agentCollaboration
            .flatMap((c) => c.agents)
            .map((agent) => {
              const match = rawSolution.aiOpportunitiesUsed?.find(
                (o) => o.ai_opportunity_id === agent.agent_id
              );
              const isMandatory = match?.must_have === 'MUST HAVE';
              return {
                label: agent.agent_name,
                mustHave: match?.must_have,
                mustHaveLabel: isMandatory ? 'Essential' : 'Optional',
                className: isMandatory ? 'yellow' : 'blue',
              };
            }),
        }
      : undefined;
    const impact = rawSolution.impact;
    return {
      ...rawSolution,
      allDataRequirements,
      allTopBenefits,
      keyConsiderations,
      allAgents,
      impact,
    };
  }, []);

  useEffect(() => {
    const vaultState = aiSolutionData?.vault ?? selectedSolutionFromLibrary?.vault ?? null;
    setLocalVaultState(vaultState);
  }, [aiSolutionData?.vault, selectedSolutionFromLibrary?.vault]);
  const handleVaultClick = useCallback(async () => {
    if (isVaultActionLoading) return;
    setIsVaultActionLoading(true);
    try {
      const result = handleToggleVault?.();
      await Promise.resolve(result);
    } catch (err) {
      console.error('Vault action failed', err);
    } finally {
      setTimeout(() => setIsVaultActionLoading(false), 300);
    }
  }, [handleToggleVault, isVaultActionLoading]);

  const fetchSolutionById = useCallback(async (): Promise<void> => {
    if (!selectedSolutionId) {
      return;
    }

    if (String(selectedSolutionId).startsWith('mock-')) {
      setQueryLoading(false);
      return;
    }

    setQueryLoading(true);
    setQueryError(null);
    setLlmResponseData([]);

    try {
      // Only call fetchAISolutionWithAgentEnablement for ideation or custom processes
      // For default processes, use agent key and don't call this API
      const shouldCallAgentEnablementAPI = isIdeation || processType === 'custom';

      let response;
      if (shouldCallAgentEnablementAPI) {
        response = await fetchAISolutionWithAgentEnablement({
          solutionId: String(selectedSolutionId),
          diagramId: diagramId || '',
          organizationId: currentOrg,
        });
      } else {
        // For default processes, use fetchAISolutionByID instead
        response = await fetchAISolutionByID({
          solutionId: String(selectedSolutionId),
          diagramId: diagramId || '',
          organizationId: currentOrg,
        });
      }

      if (!response) {
        throw new Error('Invalid response structure');
      }

      setAiSolutionData?.(response);
    } catch (err: unknown) {
      setQueryError(err);
      throw err;
    } finally {
      setQueryLoading(false);
    }
  }, [selectedSolutionId, diagramId, currentOrg, setAiSolutionData, isIdeation, processType]);

  useEffect(() => {
    if (aiSolutionData && Object.keys(aiSolutionData).includes('agent')) {
      setUpdatedAiSolution(aiSolutionData);
    }
  }, [aiSolutionData]);

  useEffect(() => {
    if (selectedSolutionId) {
      fetchSolutionById();
    }
  }, [selectedSolutionId, fetchSolutionById]);

  useEffect(() => {
    if (queryLoading) return;
    if (queryError) return;
    if (aiSolutionData) {
      const rawSolution = aiSolutionData;
      const enhancedSolution = processSolutionData(rawSolution);
      setSolution(enhancedSolution);
      setIsFavorite(rawSolution.favorite || false);

      // Initialize agent data from API response if available
      if (aiSolutionData.agent) {
        const agentData = aiSolutionData.agent;

        // Set agent response data for display
        if (
          agentData.ai_enablement ||
          agentData.agent_classification ||
          agentData.embedded ||
          agentData.extended ||
          agentData.custom
        ) {
          const agentResponse =
            agentData.agent_classification ||
            agentData.embedded ||
            agentData.extended ||
            agentData.custom ||
            agentData.ai_enablement ||
            [];
          setAgentLlmResponse(agentResponse);

          const llmData = agentData.ai_solution || agentData.application_in_use || [];
          setLlmResponseData(llmData);
        }
      }
      setApprovalStatus(rawSolution.status || null);
    }
    setUpdatedSolutionData(aiSolutionData);
  }, [aiSolutionData, updatedAiSolution, queryLoading, queryError, processSolutionData]);

  // Fetch enterprise technology and sync agent data
  useEffect(() => {
    const fetchEnterpriseTechnology = async () => {
      if (!aiSolutionData || !aiSolutionData.id) {
        return;
      }

      try {
        setIsLoadingEnterpriseTech(true);
        setLlmResponseLoading(true);

        // Always fetch fresh enterprise technology data to detect updates when landscape changes
        console.log('Fetching fresh enterprise technology data for sync decision');

        // Always fetch enterprise technology first to check if it's available
        const enterpriseTechResponse = await getEnterpriseTechnology(currentOrg);

        // Check if organization has enterprise technology configured
        // If enterprise technology exists, we should try to get agent data
        const hasEnterpriseTech =
          enterpriseTechResponse &&
          enterpriseTechResponse.industry_data &&
          Object.keys(enterpriseTechResponse.industry_data).length > 0;

        if (!hasEnterpriseTech) {
          console.log('No enterprise technology configured');
          return;
        }

        // If enterprise technology exists, always try to get agent data
        // First check if agent data already exists and is usable
        const hasExistingAgentData =
          aiSolutionData.agent &&
          (aiSolutionData.agent.ai_enablement ||
            aiSolutionData.agent.agent_classification ||
            aiSolutionData.agent.embedded ||
            aiSolutionData.agent.extended ||
            aiSolutionData.agent.custom);

        if (hasExistingAgentData) {
          // Agent data exists - check if it needs updating based on timestamps
          if (aiSolutionData.agent.updated_at) {
            const shouldSync = await checkIfSyncNeeded(
              enterpriseTechResponse,
              aiSolutionData.agent
            );

            if (shouldSync) {
              setIsLoadingAgentLlmResponse(true);

              try {
                const syncResponse = await makeAgentSyncAPICall(
                  aiSolutionData,
                  enterpriseTechResponse
                );
                // Update data with sync response - prioritize different response formats
                setAgentLlmResponse(
                  syncResponse?.agent_classification ||
                    syncResponse?.embedded ||
                    syncResponse?.extended ||
                    syncResponse?.custom ||
                    []
                );
                setLlmResponseData(
                  syncResponse?.ai_solution || syncResponse?.application_in_use || []
                );
              } catch (error) {
                console.error('Sync failed:', error);
                // Fall back to existing data from fetchAISolutionByID
                setAgentLlmResponse(
                  aiSolutionData.agent.agent_classification ||
                    aiSolutionData.agent.embedded ||
                    aiSolutionData.agent.extended ||
                    aiSolutionData.agent.custom ||
                    aiSolutionData.agent.ai_enablement ||
                    []
                );
                setLlmResponseData(
                  aiSolutionData.agent?.ai_solution ||
                    aiSolutionData.agent?.application_in_use ||
                    []
                );
              } finally {
                setIsLoadingAgentLlmResponse(false);
              }
            } else {
              console.log('Agent is up to date, no sync needed');
              // Set data from existing agent data from fetchAISolutionByID
              setAgentLlmResponse(
                aiSolutionData.agent.agent_classification ||
                  aiSolutionData.agent.embedded ||
                  aiSolutionData.agent.extended ||
                  aiSolutionData.agent.custom ||
                  aiSolutionData.agent.ai_enablement ||
                  []
              );
              setLlmResponseData(
                aiSolutionData.agent?.ai_solution || aiSolutionData.agent?.application_in_use || []
              );
              setIsLoadingAgentLlmResponse(false);
            }
          } else {
            console.log('Agent has no timestamp, using existing data');
            // Set data from existing agent data from fetchAISolutionByID when no timestamp
            setAgentLlmResponse(
              aiSolutionData.agent.agent_classification ||
                aiSolutionData.agent.embedded ||
                aiSolutionData.agent.extended ||
                aiSolutionData.agent.custom ||
                aiSolutionData.agent.ai_enablement ||
                []
            );
            setLlmResponseData(
              aiSolutionData.agent?.ai_solution || aiSolutionData.agent?.application_in_use || []
            );
            setIsLoadingAgentLlmResponse(false);
          }
        } else {
          // No agent data available - call sync API to generate it
          console.log('No agent data available, calling sync API to generate it');

          setIsLoadingAgentLlmResponse(true);

          try {
            const syncResponse = await makeAgentSyncAPICall(
              aiSolutionData,
              enterpriseTechResponse,
              {
                llm_agent: aiSolutionData.llm_agent,
                solution_enabler: aiSolutionData.solution_enabler,
                diagram_id: diagramId,
                organization_id: currentOrg,
                type: ENVIRONMENT_TYPE,
              }
            );

            // Update data with sync response
            setLlmResponseData(syncResponse?.application_in_use || syncResponse?.ai_solution || []);
            setAgentLlmResponse(syncResponse?.agent_classification || syncResponse?.embedded || []);

            setUpdatedSolutionData((prev: any) => ({
              ...prev,
              agent: {
                ...prev?.agent,
                ai_enablement: syncResponse.ai_enablement,
                ai_solution:
                  syncResponse.application_in_use ||
                  syncResponse.ai_solution ||
                  prev?.agent?.ai_solution ||
                  [],
                embedded:
                  syncResponse.agent_classification ||
                  syncResponse.embedded ||
                  prev?.agent?.embedded ||
                  [],
              },
            }));
          } catch (error) {
            console.error('Failed to generate agent data:', error);
          } finally {
            setIsLoadingAgentLlmResponse(false);
          }

          // Mark as synced
          if (aiSolutionData?.id) {
            globalSyncedSolutions.set(aiSolutionData.id, Date.now());
          }
        }

        // Mark as synced
        if (aiSolutionData?.id) {
          globalSyncedSolutions.set(aiSolutionData.id, Date.now());
        }
      } catch (error) {
        console.error('Failed to fetch enterprise technology:', error);
        // Reset the flag on error so it can be retried
        hasFetchedEnterpriseTechRef.current = false;
      } finally {
        setIsLoadingEnterpriseTech(false);
        setLlmResponseLoading(false);
        setIsLoadingAgentLlmResponse(false);
      }
    };

    fetchEnterpriseTechnology();
  }, [currentOrg, updatedAiSolution, diagramId]);

  const handleToggleFavorite = useCallback(() => {
    const newFavoriteState = !isFavorite;
    setIsFavorite(newFavoriteState);
    mutate({
      variables: { v1: { id: selectedSolutionId, favorite: newFavoriteState } },
    });
    setShowFavoriteModal(false);
  }, [isFavorite, mutate, selectedSolutionId]);

  useEffect(() => {
    if (currentSolutionIdRef.current !== selectedSolutionId) {
      currentSolutionIdRef.current = selectedSolutionId;
      hasLoadedImplementationRef.current = false;
      hasFetchedEnterpriseTechRef.current = false;
      setDataSources('');
      setImplementationConsiderations('');
      setSource([]);

      // Reset admin sync state
      setAgentClickCount(0);
      setLastClickTime(0);
      setIsManualSyncLoading(false);
    }
  }, [selectedSolutionId]);

  useEffect(() => {
    // Enterprise context application is not needed in this simplified component
    if (enterpriseContextApplied) {
      setEnterpriseContextApplied(false);
    }
  }, [enterpriseContextApplied, setEnterpriseContextApplied]);
  //  const IMPLEMENTATION_STYLES: Record<string, { bg: string; color: string; icon: string }> = {
  //   'Client Specific': {
  //     bg: '#F1FFEE',
  //     color: '#27742A',
  //     icon: '/images/client_specific.svg',
  //   },
  //   Embedded: {
  //     bg: '#FFF7F3',
  //     color: '#9A5A37',
  //     icon: '/images/embedded_icon.svg',
  //   },
  //   Extended: {
  //     bg: '#FFF7DF',
  //     color: '#B18600',
  //     icon: '/images/extended_icon.svg',
  //   },
  // };

  // const renderInfoBox = ({
  //   title,
  //   titleColor,
  //   icon,
  //   text,
  //   bg,
  //   extraSx = {},
  // }: {
  //   title: string;
  //   titleColor: string;
  //   icon: string;
  //   text: string;
  //   bg: string;
  //   extraSx?: object;
  // }) => {
  //   return (
  //     <Box
  //       sx={{
  //         background: bg,
  //         borderRadius: '6px',
  //         padding: '12px',
  //         mt: '18px',
  //         width: { xs: '100%', sm: '50%' },
  //         minHeight: '126px',
  //         ...extraSx,
  //       }}
  //     >
  //       <Box sx={{ display: 'flex' }}>
  //         <img src={icon} alt="icon" />
  //         <Typography sx={{ fontWeight: 500, fontSize: '15px', color: titleColor, ml: '8px' }}>
  //           {title}
  //         </Typography>
  //       </Box>
  //       <Typography sx={{ fontSize: '15px', fontWeight: 400, mt: '10px' }}>{text}</Typography>
  //     </Box>
  //   );
  // };

  useEffect(() => () => setAiSolutionData?.(null), [setAiSolutionData]);

  const getSvgPath = (type: string) => {
    switch (type) {
      case 'HIGH':
        return '/images/GreenDot.svg';
      case 'MEDIUM':
        return '/images/YellowDot.svg';
      case 'LOW':
        return '/images/RedDot.svg';
      default:
        return '/images/GreenDot.svg';
    }
  };

  const handleEdit = (type: EditType, item?: any) => {
    setEditItem(item || null);
    setOpenEditModal(true);
    setEditType(type);
  };

  const handleDiscard = () => {
    // Reset to original data
    setUpdatedSolutionData(aiSolutionData);
    setIsEditing(false);
  };

  const handleSaveSolution = (data: any) => {
    handleUpdateSolution(data);
  };

  const handleSaveClick = async () => {
    // Call parent's save handler which will update data and close edit mode
    handleSave(updatedSolutionData);

    // API call to send for approval
    if (aiSolutionData.status === 'approved') {
      const url = `${API_BASE}/api/v1/e2e_taxonomy/new_ai_opportunity/${selectedSolutionId}/`;
      await httpService(httpConstants.METHOD_TYPE.PATCH, url, {
        status: 'approval_pending',
        isAddOpportunityStatusHistory: true,
      });
    }
    setApprovalStatus('approval_pending');
    setIsEditing(false);
  };

  // Manual sync function for admin triple-click
  const triggerManualSync = useCallback(async () => {
    if (!aiSolutionData || isManualSyncLoading) return;

    setIsManualSyncLoading(true);
    console.log('🔄 Admin manual sync triggered');

    try {
      const enterpriseTechResponse = await getEnterpriseTechnology(currentOrg);
      const additionalPayload = {
        llm_agent: aiSolutionData?.llm_agent,
        solution_enabler: aiSolutionData?.solution_enabler,
        diagram_id: diagramId,
        organization_id: currentOrg,
        type: ENVIRONMENT_TYPE,
      };

      const syncResponse = await makeAgentSyncAPICall(
        aiSolutionData,
        enterpriseTechResponse,
        additionalPayload
      );

      // Map sync response to frontend state
      if (syncResponse?.embedded) {
        setAgentLlmResponse(syncResponse.embedded);
      } else if (syncResponse?.agent_classification) {
        setAgentLlmResponse(syncResponse.agent_classification);
      }
      if (syncResponse?.ai_solution) {
        setLlmResponseData(syncResponse.ai_solution);
      } else if (syncResponse?.application_in_use) {
        setLlmResponseData(syncResponse.application_in_use);
      }

      console.log('✅ Admin manual sync completed successfully');
    } catch (error) {
      console.error('❌ Admin manual sync failed:', error);
    } finally {
      setIsManualSyncLoading(false);
    }
  }, [updatedAiSolution, currentOrg, diagramId, isManualSyncLoading]);

  // Handle triple-click on Agents tab for admin sync
  const handleAgentTabClick = useCallback(
    (tabKey: string) => {
      const currentTime = Date.now();

      if (tabKey === 'agents') {
        // Reset count if more than 2 seconds have passed
        if (currentTime - lastClickTime > 2000) {
          setAgentClickCount(1);
          console.log('🔄 Admin sync: First click on Agents tab');
        } else {
          const newCount = agentClickCount + 1;
          setAgentClickCount(newCount);
          console.log(`🔄 Admin sync: Click ${newCount}/3 on Agents tab`);
        }

        setLastClickTime(currentTime);

        // Trigger manual sync on 3rd click
        if (agentClickCount + 1 === 3) {
          console.log('🚀 Admin sync: Triple-click detected! Triggering manual sync...');
          triggerManualSync();
          setAgentClickCount(0); // Reset count
        }
      } else {
        // Reset count if clicking on other tabs
        if (agentClickCount > 0) {
          console.log('🔄 Admin sync: Switched tabs, resetting click count');
          setAgentClickCount(0);
        }
      }

      // Always update the active piece
      setActivePiece(tabKey);
    },
    [agentClickCount, lastClickTime, triggerManualSync]
  );

  useEffect(() => {
    return () => {
      setActivePiece('information');
    };
  }, []);

  // Use the appropriate data source based on edit mode
  const displayData = updatedSolutionData || aiSolutionData;

  // Check if agent data exists in the API response
  const hasAgentDataInResponse =
    aiSolutionData?.agent &&
    (aiSolutionData.agent.ai_enablement ||
      aiSolutionData.agent.agent_classification ||
      aiSolutionData.agent.embedded ||
      aiSolutionData.agent.extended ||
      aiSolutionData.agent.custom);

  const normalizedAiSolutionData = () => {
    let agentdata: {
      embedded: { capability_name: string; description: string }[];
      extended: { capability_name: string; description: string }[];
      custom: { capability_name: string; description: string }[];
    } = { embedded: [], extended: [], custom: [] };
    if (updatedAiSolution || aiSolutionData) {
      if (window.location.pathname.includes('ideation')) {
        agentdata = displayData?.agent?.ai_enablement?.enablement_matrix;
      } else if (window.location.pathname.includes('process-xplr')) {
        displayData?.agent?.embedded.map(
          (item: { implementation_type: string; agent_name: string; justification: string }) => {
            if (item.implementation_type === 'Extended') {
              agentdata.extended.push({
                capability_name: item.agent_name,
                description: item.justification,
              });
            } else if (item.implementation_type === 'Embedded') {
              agentdata.embedded.push({
                capability_name: item.agent_name,
                description: item.justification,
              });
            } else if (item.implementation_type === 'Client Specific') {
              agentdata.custom.push({
                capability_name: item.agent_name,
                description: item.justification,
              });
            }
          }
        );
      }
      return agentdata;
    }
  };
  useEffect(() => {
    if (!displayData) return;
    displayData.agentData = normalizedAiSolutionData();
    console.log('displayData', displayData);
  }, [displayData]);

  // Data Requirements rendering function
  const renderDataRequirementsContent = useMemo(() => {
    type DataSource = { name: string; type: string; technologies?: string[] };

    // Parse description_llm safely
    let descriptionAssets: { data_asset: string; technology: string[] }[] = [];
    try {
      const aiSolution = displayData?.agent?.ai_solution;
      descriptionAssets = llmResponseData && Array.isArray(aiSolution) ? aiSolution : [];
    } catch (err) {
      console.error('Invalid description_llm JSON', err);
      descriptionAssets = [];
    }

    // Helper to transform arrays into {name, type} while preserving technologies from description_llm
    const transformSources = (items: string[] | undefined, type: string): DataSource[] =>
      (items || []).map((name) => {
        const matched = descriptionAssets.find(
          (a) => a.data_asset?.toLowerCase() === name.toLowerCase()
        );

        return {
          name,
          type,
          technologies: Array.isArray(matched?.technology) ? matched.technology : [],
        };
      });

    // Use data_sources from displayData response
    const internalSources: DataSource[] = [
      ...transformSources(displayData?.data_sources?.internal?.structured, 'Structured'),
      ...transformSources(displayData?.data_sources?.internal?.unstructured, 'Unstructured'),
    ];

    const externalSources: DataSource[] = [
      ...transformSources(displayData?.data_sources?.external?.structured, 'Structured'),
      ...transformSources(displayData?.data_sources?.external?.unstructured, 'Unstructured'),
    ];

    const renderChip = (label: string) => (
      <Typography
        key={label}
        variant="caption"
        sx={{
          borderRadius: '12px',
          px: 1.5,
          py: 0.5,
          borderWidth: '0.5px',
          borderStyle: 'solid',
          borderColor: '#DDE1E6',
          fontSize: '12px',
          lineHeight: '14px',
          color: 'text.primary',
          textAlign: 'center',
          ml: 1,
        }}
      >
        {label}
      </Typography>
    );

    const renderSourceItem = (item: DataSource, sourceCategory: 'internal' | 'external') => {
      return (
        <Box
          key={`${item.name}`}
          sx={{ mb: 1, display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', flexWrap: 'nowrap' }}>
            <img
              src="/images/db.svg"
              alt=""
              style={{ width: 16, height: 16, padding: 4, marginTop: 2 }}
            />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '16px', mr: 1 }}>
              {item.name}
            </Typography>
          </Box>
          {renderChip(item.type)}
          {llmResponseLoading ? (
            <Typography
              sx={{
                borderRadius: '12px',
                px: 1,
                borderWidth: '0.5px',
                borderStyle: 'solid',
                borderColor: '#DDE1E6',
                fontSize: '12px',
                lineHeight: '14px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                ml: 1,
                height: '22px',
              }}
            >
              <Skeleton variant="rectangular" height={8} width={70} sx={{ borderRadius: '12px' }} />
            </Typography>
          ) : (
            <Typography sx={{ mt: '4px' }}>
              {item.technologies?.map((tech) => renderChip(tech))}
            </Typography>
          )}
          {isEditing && (
            <DriveFileRenameOutlineIcon
              sx={{ cursor: 'pointer', height: '16px', width: '16px', color: 'grey.500' }}
              onClick={() =>
                handleEdit('data_requirements', {
                  name: item.name,
                  originalName: item.name,
                  type: item.type,
                  sourceCategory: sourceCategory,
                  technologies: item.technologies,
                })
              }
            />
          )}
        </Box>
      );
    };

    if (internalSources.length || externalSources.length) {
      return (
        <Grid container direction="column">
          {internalSources.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '16px', mb: 2 }}>
                Internal Data Sources
              </Typography>
              {internalSources.map((item) => renderSourceItem(item, 'internal'))}
            </Box>
          )}

          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '16px', mb: 2 }}>
              External Data Sources
            </Typography>
            {externalSources.length > 0 ? (
              externalSources.map((item) => renderSourceItem(item, 'external'))
            ) : (
              <Typography variant="body2" sx={{ fontWeight: 400, fontSize: '14px' }}>
                None
              </Typography>
            )}
          </Box>
        </Grid>
      );
    }

    if (isLoadingImplementation) {
      return (
        <Grid container spacing={4}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {[...Array(8)].map((_, index) => (
              <Box key={index} sx={{ height: '1.5em', bgcolor: 'grey.200', borderRadius: 1 }} />
            ))}
          </Box>
        </Grid>
      );
    }

    return (
      <Grid container spacing={4}>
        <Typography>No data requirements available</Typography>
      </Grid>
    );
  }, [displayData, isLoadingImplementation, llmResponseLoading, llmResponseData, isEditing]);

  const renderImplementationConsiderationsContent = useMemo(() => {
    const keyConsiderationsList = displayData?.key_requirements || [];

    if (keyConsiderationsList.length) {
      return (
        <Grid container spacing={4}>
          <Grid size={{ xs: 12, md: 12 }}>
            <Box>
              {keyConsiderationsList.map((item: string, idx: number) => (
                <Box
                  key={idx}
                  sx={{ display: 'flex', alignItems: 'flex-start', my: 1, minWidth: 0 }}
                >
                  <Box sx={{ pt: '3px', flexShrink: 0, mr: 1 }}>
                    <img src="/images/Info_implement.svg" />
                  </Box>

                  <Typography
                    variant="body1"
                    sx={{
                      display: 'block',
                      whiteSpace: 'pre-line',
                      textAlign: 'left',
                      m: 0,
                      pl: 0,
                      textIndent: 0,
                    }}
                  >
                    {item}
                    {isEditing && (
                      <DriveFileRenameOutlineIcon
                        sx={{ cursor: 'pointer', height: '16px', width: '16px', color: 'grey.500' }}
                        onClick={() => handleEdit('key_requirements', { index: idx, name: item })}
                      />
                    )}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Grid>
        </Grid>
      );
    }

    return (
      <Grid container spacing={4}>
        <Grid size={{ xs: 12, md: 12 }}>
          <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>
            No key requirements available
          </Typography>
        </Grid>
      </Grid>
    );
  }, [displayData?.key_requirements, isLoadingImplementation, isEditing]);

  if (queryLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '80vh',
          width: '100%',
        }}
      >
        <Loader message="Fetching AI Opportunity" />
      </Box>
    );
  }

  if (queryError) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}
      >
        <Typography color="error">Error loading solution data</Typography>
      </Box>
    );
  }

  if (!displayData || !selectedSolutionId) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '80vh',
          width: '100%',
        }}
      >
        <Typography>No solution data available. Please select a solution.</Typography>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        <Typography variant="h6">
          {isIdeation ? 'AI Ideation Opportunity Details' : 'AI Opportunity Details'}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {isEditable && isProcessDiagramActive ? (
            <>
              {/* Show approval controls only when NOT in editing mode */}
              {!isEditing && isProcessDiagramActive && (
                <>
                  {approvalStatus === 'created' && (
                    <Button
                      variant="text"
                      onClick={handleSendForApproval}
                      sx={{
                        textTransform: 'none',
                        color: '#1A55F9',
                        fontWeight: 500,
                        px: 2,
                        minWidth: 0,
                      }}
                      startIcon={<CheckCircleOutline />}
                    >
                      Send for approval
                    </Button>
                  )}
                </>
              )}

              {!isEditing ? (
                <Box
                  sx={{
                    color: '#1A55F9',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <img src="/images/edit-border.png" alt="edit" width={14} />
                  <Typography
                    variant="button"
                    sx={{ textTransform: 'capitalize' }}
                    onClick={() => setIsEditing(true)}
                  >
                    Edit
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Button variant="outlined" size="small" onClick={handleDiscard}>
                    Discard
                  </Button>
                  <Button variant="contained" size="small" onClick={handleSaveClick}>
                    Save
                  </Button>
                </Box>
              )}
              {!isEditing && isProcessDiagramActive && approvalStatus === 'created' && (
                <Button
                  variant="text"
                  onClick={() => setShowDeleteConfirmModal(true)}
                  sx={{
                    textTransform: 'none',
                    color: '#EF4444',
                    fontWeight: 500,
                    px: 2,
                    minWidth: 0,
                  }}
                  startIcon={<img src="/images/removeOpportunity.svg" alt="delete" />}
                >
                  Remove
                </Button>
              )}
              {closeDrawer && <CloseIcon onClick={closeDrawer} sx={{ cursor: 'pointer' }} />}
            </>
          ) : null}
          {!isProcessDiagramActive && (
            <Button
              variant="outlined"
              startIcon={<img src="/images/Vault.png" alt="vault" />}
              onClick={handleVaultClick}
              disabled={isVaultActionLoading}
              sx={{
                textTransform: 'none',
                fontSize: '14px',
                fontWeight: 500,
              }}
            >
              {`${(localVaultState ?? aiSolutionData?.vault ?? selectedSolutionFromLibrary?.vault) ? 'Remove from' : 'Add to'} Solution Vault`}{' '}
              {isVaultActionLoading && <span style={{ marginLeft: 8, opacity: 0.7 }}>…</span>}
            </Button>
          )}
        </Box>
      </Box>
      <Box
        sx={{
          m: 2,
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          border: '1px solid #e0e0e0',
          borderRadius: 1,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          <Typography variant="caption" sx={{ color: '#1A55F9' }}>
            {displayData?.solution_enabler}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Typography variant="h6">{displayData?.opportunity_name}</Typography>
            {isEditing && (
              <DriveFileRenameOutlineIcon
                sx={{
                  cursor: 'pointer',
                  height: '16px',
                  width: '16px',
                  color: 'grey.500',
                }}
                onClick={() => handleEdit('name')}
              />
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {isIdeation && (
                <Box
                  sx={{
                    background: '#ffffff',
                    borderRadius: '39px',
                    border: '1px solid #1B3FD1',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: 2,
                    height: '28px',
                    boxShadow: 1,
                  }}
                >
                  <Typography
                    sx={{
                      color: '#1B3FD1',
                      fontFamily: 'Poppins',
                      fontWeight: 500,
                      fontSize: '14px',
                      lineHeight: '120%',
                      textTransform: 'uppercase',
                    }}
                  >
                    {String(displayData?.impact)}
                  </Typography>
                </Box>
              )}

              {isEditing && (
                <DriveFileRenameOutlineIcon
                  sx={{
                    cursor: 'pointer',
                    fontSize: 18,
                    color: 'grey.500',
                    mt: '2px',
                  }}
                  onClick={() => handleEdit('impact')}
                />
              )}
            </Box>
          </Box>
        </Box>
        {!isProcessDiagramActive && (
          <>
            {isMobile && (
              <IconButton onClick={() => handleToggleVault()}>
                <img src="/images/Vault.png" alt="vault" style={{ width: 24, height: 24 }} />
              </IconButton>
            )}
          </>
        )}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          {isIdeation && (
            <>
              {(() => {
                const statusConfig = getApprovalStatusConfig(approvalStatus);
                if (!statusConfig.show) return null;

                return (
                  <Chip
                    label={statusConfig.text}
                    sx={{
                      height: '28px',
                      borderRadius: '4px',
                      fontFamily: 'Poppins',
                      fontWeight: 500,
                      fontSize: '12px',
                      color: statusConfig.color,
                      backgroundColor: statusConfig.bgColor,
                      border: 'none',
                      '& .MuiChip-label': {
                        padding: '1px 6px',
                      },
                    }}
                  />
                );
              })()}
            </>
          )}
          {!isIdeation && (
            <Box
              sx={{
                background: 'linear-gradient(90deg, #1532B8 24.06%, #1B54F8 100%)',
                borderRadius: '39px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: { xs: '50%', md: '161px' },
                height: '33px',
                opacity: 1,
                boxShadow: 1,
                mr: { md: !isEditing ? 3 : 0 },
                mt: { xs: 2, md: 0 },
              }}
            >
              <Typography
                sx={{
                  color: 'white',
                  fontFamily: 'Poppins',
                  fontWeight: 500,
                  fontSize: '16px',
                  lineHeight: '120%',
                  letterSpacing: '-1%',
                  textAlign: 'center',
                  textTransform: 'uppercase',
                }}
              >
                {String(displayData?.impact)}
              </Typography>
            </Box>
          )}
          {isEditing && !isIdeation && processType !== 'custom' && (
            <DriveFileRenameOutlineIcon
              sx={{
                cursor: 'pointer',
                height: '16px',
                width: '16px',
                color: 'grey.500',
              }}
              onClick={() => handleEdit('impact')}
            />
          )}
        </Box>
      </Box>
      <Grid container spacing={2} sx={{ mb: 15, p: 2 }}>
        <Grid size={{ xs: 12, md: 12 }} spacing={2}>
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <CardContent
              sx={{
                border: '1px solid #DDE1E6',
                boxShadow: 'none',
                borderRadius: '10px',
                mb: 2,
                p: 0,
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {[
                  { key: 'information', label: 'Key Information' },
                  {
                    key: 'agents',
                    label: isIdeation
                      ? 'AI Enablement'
                      : processType === 'custom'
                        ? 'AI Enablement'
                        : 'Agents',
                  },
                ].map((tab, index) => (
                  <Box
                    key={tab.key}
                    sx={{
                      flex: 1,
                      height: '64px',
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      fontSize: '16px',
                      cursor: 'pointer',
                      fontWeight: 600,
                      borderLeft: index === 1 ? '1px solid #DDE1E6' : 'none',
                    }}
                    onClick={() => handleAgentTabClick(tab.key)}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        color: activePiece === tab.key ? '#1A55F9' : '#000000',
                        borderBottom: activePiece === tab.key ? '2px solid #1A55F9' : 'none',
                        pb: activePiece !== tab.key ? '2px' : 0,
                        fontSize: '16px',
                      }}
                    >
                      {tab.label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>

            {activePiece === 'information' ? (
              <Grid
                container
                spacing={2}
                sx={{
                  height: 'calc(100vh - 300px)',
                }}
              >
                <Grid
                  size={{ xs: 12, md: 9 }}
                  sx={{
                    height: '100%',
                    overflowY: 'auto',
                    scrollbarWidth: 'none',
                    msOverflowStyle: 'none',
                    '&::-webkit-scrollbar': {
                      display: 'none',
                    },
                    // pr: 1,
                  }}
                >
                  {/* <Card sx={{ mb: 3, boxShadow: 'none' }}>
                  <CardContent
                    sx={{ border: '1px solid #DDE1E6', boxShadow: 'none', borderRadius: '10px' }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        width: '100%',
                      }}
                    >
                      {(() => {
                        const hasDetails =
                          Boolean(displayData?.roi_description) ||
                          Boolean(displayData?.complexity_description);

                        const overviewWidth = hasDetails ? '35%' : '60%';
                        const roiWidth = hasDetails ? '32.5%' : '20%';
                        const complexityWidth = hasDetails ? '32.5%' : '20%';

                        const DividerBox = () => (
                          <Box
                            sx={{
                              width: { xs: '100%', md: '1px' },
                              height: { xs: '1px', md: 'auto' },
                              backgroundColor: '#DDDFDE',
                              my: { xs: 1, md: 0 },
                            }}
                          />
                        );

                        return (
                          <>
                            <Box
                              sx={{
                                flexBasis: overviewWidth,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                                gap: 1,
                                pr: 2,
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                  Overview
                                </Typography>
                                {isEditing && (
                                  <DriveFileRenameOutlineIcon
                                    sx={{
                                      cursor: 'pointer',
                                      height: '16px',
                                      width: '16px',
                                      color: 'grey.500',
                                    }}
                                    onClick={() => handleEdit('overview')}
                                  />
                                )}
                              </Box>

                              <Typography
                                variant="body2"
                                color="#000000"
                                sx={{ lineHeight: 1.6, fontSize: '16px' }}
                              >
                                {(() => {
                                  const text = displayData?.description || '';
                                  const sentences = text
                                    .split('.')
                                    .map((s: string) => s.trim())
                                    .filter(Boolean);
                                  if (sentences.length === 0) return text;
                                  if (sentences.length === 1) return sentences[0] + '.';
                                  return sentences.slice(0, 2).join('. ') + '.';
                                })()}
                              </Typography>
                            </Box>

                            <DividerBox />
                            <Box
                              sx={{
                                flexBasis: roiWidth,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: displayData?.roi_description ? 'flex-start' : 'center',
                                justifyContent: displayData?.roi_description
                                  ? 'flex-start'
                                  : 'center',
                                textAlign: displayData?.roi_description ? 'left' : 'center',
                                gap: 1,
                                px: 2,
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography
                                  sx={{ fontWeight: 400, fontSize: '20px', color: '#4D5358' }}
                                >
                                  ROI
                                </Typography>
                                {isEditing && (
                                  <DriveFileRenameOutlineIcon
                                    sx={{
                                      cursor: 'pointer',
                                      height: '16px',
                                      width: '16px',
                                      color: 'grey.500',
                                    }}
                                    onClick={() => handleEdit('roi')}
                                  />
                                )}
                              </Box>
                              <Typography
                                sx={{
                                  fontWeight: 600,
                                  fontSize: '18px',
                                  display: 'flex',
                                  alignItems: 'center',
                                }}
                              >
                                <img
                                  src={getSvgPath(displayData?.roi)}
                                  alt=""
                                  style={{ width: 10, height: 10, marginRight: 3 }}
                                />
                                {displayData?.roi}
                              </Typography>
                              {displayData?.roi_description && (
                                <Box
                                  sx={{
                                    width: '100%',
                                    //maxWidth: '170px',
                                    overflowWrap: 'break-word',
                                  }}
                                >
                                  <Typography sx={{ fontSize: '16px', fontWeight: 400 }}>
                                    {displayData?.roi_description}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                            <DividerBox />
                            <Box
                              sx={{
                                flexBasis: complexityWidth,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: displayData?.complexity_description
                                  ? 'flex-start'
                                  : 'center',
                                justifyContent: displayData?.complexity_description
                                  ? 'flex-start'
                                  : 'center',
                                textAlign: displayData?.complexity_description ? 'left' : 'center',
                                gap: 1,
                                pl: 2,
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography
                                  sx={{ fontWeight: 400, fontSize: '20px', color: '#4D5358' }}
                                >
                                  Complexity
                                </Typography>
                                {isEditing && (
                                  <DriveFileRenameOutlineIcon
                                    sx={{
                                      cursor: 'pointer',
                                      height: '16px',
                                      width: '16px',
                                      color: 'grey.500',
                                    }}
                                    onClick={() => handleEdit('complexity')}
                                  />
                                )}
                              </Box>
                              <Typography
                                sx={{
                                  fontWeight: 600,
                                  fontSize: '18px',
                                  display: 'flex',
                                  alignItems: 'center',
                                }}
                              >
                                <img
                                  src={getSvgPath(displayData?.complexity)}
                                  alt=""
                                  style={{ width: 10, height: 10, marginRight: 3 }}
                                />
                                {displayData?.complexity}
                              </Typography>
                              {displayData?.complexity_description && (
                                <Box
                                  sx={{
                                    width: '100%',
                                    overflowWrap: 'break-word',
                                  }}
                                >
                                  <Typography sx={{ fontSize: '16px', fontWeight: 400 }}>
                                    {displayData?.complexity_description}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </>
                        );
                      })()}
                    </Box>
                  </CardContent>
                </Card> */}

                  <Card sx={{ mb: 2, boxShadow: 'none', border: '1px solid #E0E0E0' }}>
                    <CardContent>
                      {/* OVERVIEW */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography sx={{ fontWeight: 600, mb: 1, fontSize: '18px' }}>
                          Overview
                        </Typography>
                        {isEditing && (
                          <DriveFileRenameOutlineIcon
                            sx={{ cursor: 'pointer', fontSize: 18, color: 'grey.600' }}
                            onClick={() => handleEdit('overview')}
                          />
                        )}
                      </Box>
                      <Typography sx={{ fontSize: '14px', lineHeight: 1.6 }}>
                        {displayData?.description}
                      </Typography>

                      {/* PERFORMANCE IMPROVEMENT */}
                      {displayData?.performance_improvement && (
                        <Box sx={{ mt: 3 }}>
                          <Typography sx={{ fontWeight: 600, mb: 1, fontSize: '18px' }}>
                            Performance Improvement Detail
                          </Typography>

                          <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                            Supports Performance Improvement :
                          </Typography>

                          <Stack spacing={1} mt={1}>
                            {(
                              displayData?.performance_improvement
                                ?.supports_performance_improvement || []
                            ).map((item: string, i: number) => (
                              <Typography
                                key={i}
                                sx={{
                                  bgcolor: '#F3F6FF',
                                  minHeight: '36px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  px: 1.5,
                                  py: 0.5,
                                  borderRadius: '4px',
                                  width: 'full',
                                  fontWeight: 500,
                                  fontSize: '14px',
                                }}
                              >
                                {item}
                              </Typography>
                            ))}
                          </Stack>
                        </Box>
                      )}

                      {/* VALUE DRIVER */}
                      {displayData?.performance_improvement?.value_driver && (
                        <Box sx={{ mt: 3 }}>
                          <Typography sx={{ fontWeight: 600, mb: 1, fontSize: '14px' }}>
                            Value Driver
                          </Typography>

                          <Typography sx={{ fontSize: '14px', lineHeight: 1.6 }}>
                            {displayData.performance_improvement.value_driver}
                          </Typography>
                        </Box>
                      )}

                      <Divider sx={{ my: 1 }} />

                      {/* ROI + COMPLEXITY */}
                      <Grid container alignItems="stretch">
                        {/* ROI */}
                        <Grid size={{ xs: 12, md: 5.9 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography sx={{ fontSize: '14px', color: '#666' }}>ROI</Typography>

                            {isEditing && (
                              <DriveFileRenameOutlineIcon
                                sx={{ cursor: 'pointer', fontSize: 16 }}
                                onClick={() => handleEdit('roi')}
                              />
                            )}
                          </Box>
                          <Typography
                            sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}
                          >
                            <img
                              src={getSvgPath(displayData?.roi)}
                              alt=""
                              style={{ width: 10, height: 10, marginRight: 3 }}
                            />
                            {displayData?.roi}
                          </Typography>
                          <Typography sx={{ fontSize: '13px' }}>
                            {displayData?.roi_description}
                          </Typography>
                        </Grid>

                        {/* VERTICAL DIVIDER */}
                        <Grid
                          size={{ xs: 12, md: 0.2 }}
                          sx={{
                            display: { xs: 'none', md: 'flex' },
                            justifyContent: 'center',
                            pr: 1,
                          }}
                        >
                          <Divider orientation="vertical" flexItem />
                        </Grid>

                        {/* COMPLEXITY */}
                        <Grid size={{ xs: 12, md: 5.9 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography sx={{ fontSize: '14px', color: '#666' }}>
                              Complexity
                            </Typography>

                            {isEditing && (
                              <DriveFileRenameOutlineIcon
                                sx={{ cursor: 'pointer', fontSize: 16 }}
                                onClick={() => handleEdit('complexity')}
                              />
                            )}
                          </Box>
                          <Typography
                            sx={{ fontWeight: 600, display: 'flex', alignItems: 'center' }}
                          >
                            <img
                              src={getSvgPath(displayData?.complexity)}
                              alt=""
                              style={{ width: 10, height: 10, marginRight: 3 }}
                            />
                            {displayData?.complexity}
                          </Typography>

                          <Typography sx={{ fontSize: '13px' }}>
                            {displayData?.complexity_description}
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>

                  <Grid container spacing={2} sx={{ flexDirection: { xs: 'column', md: 'row' } }}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            minHeight: '3rem',
                            bgcolor: '#1A55F9',
                            color: 'white',
                            justifyContent: 'space-between',
                            px: 2,
                            py: 1,
                            borderTopLeftRadius: '8px',
                            borderTopRightRadius: '8px',
                            fontWeight: 'bold',
                          }}
                        >
                          <Box
                            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                          >
                            <i className="pi pi-bullseye" style={{ marginRight: '8px' }} />
                            <img src="/images/data_sources.svg" style={{ marginRight: '8px' }} />
                            <Typography>Data Sources</Typography>
                          </Box>
                          {isEditing && (
                            <IconButton
                              size="small"
                              onClick={() => handleEdit('add_data_requirements')}
                            >
                              <AddBoxOutlinedIcon sx={{ color: 'white' }} />
                            </IconButton>
                          )}
                        </Box>
                        <Stack
                          spacing={2}
                          alignItems="baseline"
                          justifyContent="flex-start"
                          sx={{ px: 3, py: 3, border: '1px solid #ccc' }}
                        >
                          {renderDataRequirementsContent}
                        </Stack>
                      </Box>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Box sx={{ height: 'max-content', display: 'flex', flexDirection: 'column' }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            minHeight: '3rem',
                            bgcolor: '#1A55F9',
                            color: 'white',
                            justifyContent: 'space-between',
                            px: 2,
                            py: 1,
                            borderTopLeftRadius: '8px',
                            borderTopRightRadius: '8px',
                            fontWeight: 'bold',
                          }}
                        >
                          <Box
                            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                          >
                            <i className="pi pi-bullseye" style={{ marginRight: '8px' }} />
                            <img
                              src="/images/Info_implement_info.svg"
                              style={{ marginRight: '8px' }}
                            />
                            <Typography>Key Requirements</Typography>
                          </Box>
                          {isEditing && (
                            <IconButton
                              size="small"
                              onClick={() => handleEdit('add_key_requirements')}
                            >
                              <AddBoxOutlinedIcon sx={{ color: 'white' }} />
                            </IconButton>
                          )}
                        </Box>
                        <Stack
                          spacing={2}
                          alignItems="baseline"
                          justifyContent="flex-start"
                          sx={{ flexGrow: 1, px: 3, py: 3, border: '1px solid #ccc' }}
                        >
                          {renderImplementationConsiderationsContent}
                        </Stack>
                      </Box>
                    </Grid>
                  </Grid>
                </Grid>

                <Grid size={{ xs: 12, md: 3 }}>
                  <TopBenefits
                    allTopBenefits={
                      displayData?.benefits
                        ? Object.entries(displayData.benefits).map(([key, value]) => ({
                            title: getBenefitTitle(key),
                            label: getBenefitLabelNew(value as string),
                          }))
                        : []
                    }
                    isEditing={isEditing}
                    solutionData={updatedSolutionData}
                    setSolutionData={setUpdatedSolutionData}
                  />
                </Grid>
              </Grid>
            ) : isIdeationEnablementLoading ? (
              <Box
                sx={{
                  minHeight: '220px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  gap: 1.5,
                  mt: 15,
                }}
              >
                <CircularProgress />
                <Typography sx={{ fontSize: '15px', fontWeight: 600, color: '#000000' }}>
                  Please wait while we are generating{' '}
                  {isIdeation
                    ? 'AI Enablement'
                    : processType === 'custom'
                      ? 'AI Enablement'
                      : 'Agents'}
                </Typography>
              </Box>
            ) : (agentLlmResponse.length === 0 || Object.keys(agentLlmResponse).length === 0) &&
              !hasAgentDataInResponse ? (
              <Box
                sx={{
                  width: '100%',
                  minHeight: '220px',
                  bgcolor: '#F6F8FC',
                  borderRadius: '8px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  textAlign: 'center',
                  gap: 1.5,
                  px: 2,
                }}
              >
                <Typography
                  sx={{
                    fontSize: '18px',
                    fontWeight: 600,
                    color: '#000',
                  }}
                >
                  Enablement Not Available
                </Typography>

                <Typography
                  sx={{
                    fontSize: '14px',
                    color: '#5F6368',
                    maxWidth: 'full',
                    lineHeight: 1.6,
                  }}
                >
                  Configure your technology landscape to see the enablement available for this
                  opportunity.
                </Typography>

                <Button
                  variant="contained"
                  sx={{
                    mt: 1,
                    backgroundColor: '#1A55F9',
                    textTransform: 'none',
                    borderRadius: '6px',
                    px: 3,
                    py: 1,
                    fontSize: '14px',
                    fontWeight: 500,
                    '&:hover': {
                      backgroundColor: '#1746d1',
                    },
                  }}
                  onClick={() => {
                    navigate('/enterprise-context');
                  }}
                >
                  Configure Technology Landscape
                </Button>
              </Box>
            ) : (
              <Box sx={{ height: '600px', overflow: 'auto' }}>
                {isIdeation || processType === 'custom' ? (
                  // AI Enablement view for isIdeation = true or Custom process
                  <>
                    <Card sx={{ mb: 0, boxShadow: 'none' }}>
                      <CardContent
                        sx={{
                          // border: '1px solid #DDE1E6',
                          borderRadius: '10px',
                          boxShadow: 'none',
                          p: 0,
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: 2,
                            // borderBottom: '1px solid #DDE1E6',
                            cursor: 'pointer',
                          }}
                          onClick={() => {
                            const newExpanded = { ...enablementExpanded };
                            newExpanded['embedded'] = !newExpanded['embedded'];
                            setEnablementExpanded(newExpanded);
                          }}
                        >
                          <Typography sx={{ fontWeight: 600, fontSize: '18px' }}>
                            Enablement Model
                          </Typography>
                        </Box>

                        {/* Embedded Section - Dynamic */}
                        {(() => {
                          const embeddedAgents = displayData?.agentData?.embedded || [];

                          if (embeddedAgents.length === 0) return null;

                          return (
                            <Box>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  mx: 2,
                                  py: 1,
                                  cursor: 'pointer',
                                  borderBottom: '1px solid #D9D9D9',
                                  mb: 2,
                                  // bgcolor: enablementExpanded['embedded']
                                  //   ? '#F9FAFB'
                                  //   : 'transparent',
                                }}
                                onClick={() => {
                                  const newExpanded = { ...enablementExpanded };
                                  newExpanded['embedded'] = !newExpanded['embedded'];
                                  setEnablementExpanded(newExpanded);
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box
                                    component="img"
                                    src="/images/Embedded_icon1.svg"
                                    sx={{ width: 16, height: 16 }}
                                  />
                                  <Typography sx={{ fontWeight: 600, fontSize: '15px' }}>
                                    Embedded
                                  </Typography>
                                </Box>
                                <Box
                                  sx={{
                                    transform: enablementExpanded['embedded']
                                      ? 'rotate(180deg)'
                                      : 'rotate(0deg)',
                                    transition: 'transform 0.3s',
                                  }}
                                >
                                  <KeyboardArrowDownIcon />
                                </Box>
                              </Box>

                              {enablementExpanded['embedded'] && (
                                <Box sx={{ px: 2, pb: 2 }}>
                                  {isIdeationEnablementLoading ? (
                                    <Box
                                      sx={{
                                        display: 'grid',
                                        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                                        gap: 2,
                                      }}
                                    >
                                      {[1, 2, 3].map((i) => (
                                        <Box
                                          key={i}
                                          sx={{ p: 2, borderRadius: '8px', bgcolor: '#FFF7F3' }}
                                        >
                                          <Skeleton variant="text" width="60%" height={20} />
                                          <Skeleton
                                            variant="text"
                                            width="100%"
                                            height={16}
                                            sx={{ mt: 1 }}
                                          />
                                          <Skeleton variant="text" width="100%" height={16} />
                                          <Skeleton variant="text" width="80%" height={16} />
                                        </Box>
                                      ))}
                                    </Box>
                                  ) : (
                                    <Box
                                      sx={{
                                        display: 'grid',
                                        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                                        gap: 2,
                                      }}
                                    >
                                      {embeddedAgents.map((agent: CardData, index: number) => (
                                        <Box
                                          key={index}
                                          sx={{
                                            position: 'relative',
                                            borderRadius: '8px',
                                          }}
                                        >
                                          {isEditing && (
                                            <DriveFileRenameOutlineIcon
                                              sx={{
                                                position: 'absolute',
                                                top: 8,
                                                right: 8,
                                                cursor: 'pointer',
                                                height: 16,
                                                width: 16,
                                                color: 'grey.500',
                                                zIndex: 1,
                                              }}
                                              onClick={() =>
                                                handleEdit('embedded', { index: index })
                                              }
                                            />
                                          )}

                                          <EnablementCard
                                            title={agent.capability_name}
                                            content={agent.description}
                                            bgcolor="#F6F1FF"
                                          />
                                        </Box>
                                      ))}
                                    </Box>
                                  )}
                                </Box>
                              )}
                            </Box>
                          );
                        })()}

                        {/* Extended Section - Dynamic */}
                        {(() => {
                          const extendedAgents = displayData?.agentData?.extended || [];

                          if (extendedAgents.length === 0) return null;

                          return (
                            <Box>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  mx: 2,
                                  py: 1,
                                  cursor: 'pointer',
                                  borderBottom: '1px solid #D9D9D9',
                                  mb: 2,
                                  // bgcolor: enablementExpanded['extended']
                                  //   ? '#F9FAFB'
                                  //   : 'transparent',
                                }}
                                onClick={() => {
                                  const newExpanded = { ...enablementExpanded };
                                  newExpanded['extended'] = !newExpanded['extended'];
                                  setEnablementExpanded(newExpanded);
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box
                                    component="img"
                                    src="/images/extended_icon.svg"
                                    sx={{ width: 16, height: 16 }}
                                  />
                                  <Typography sx={{ fontWeight: 600, fontSize: '15px' }}>
                                    Extended
                                  </Typography>
                                </Box>
                                <Box
                                  sx={{
                                    transform: enablementExpanded['extended']
                                      ? 'rotate(180deg)'
                                      : 'rotate(0deg)',
                                    transition: 'transform 0.3s',
                                  }}
                                >
                                  <KeyboardArrowDownIcon />
                                </Box>
                              </Box>

                              {enablementExpanded['extended'] && (
                                <Box sx={{ px: 2, pb: 2 }}>
                                  {isIdeationEnablementLoading ? (
                                    <Box
                                      sx={{
                                        display: 'grid',
                                        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                                        gap: 2,
                                      }}
                                    >
                                      {[1, 2].map((i) => (
                                        <Box
                                          key={i}
                                          sx={{ p: 2, borderRadius: '8px', bgcolor: '#FFFBED' }}
                                        >
                                          <Skeleton variant="text" width="60%" height={20} />
                                          <Skeleton
                                            variant="text"
                                            width="100%"
                                            height={16}
                                            sx={{ mt: 1 }}
                                          />
                                          <Skeleton variant="text" width="100%" height={16} />
                                          <Skeleton variant="text" width="80%" height={16} />
                                        </Box>
                                      ))}
                                    </Box>
                                  ) : (
                                    <Box
                                      sx={{
                                        display: 'grid',
                                        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                                        gap: 2,
                                      }}
                                    >
                                      {extendedAgents.map((agent: CardData, index: number) => (
                                        <Box
                                          key={index}
                                          sx={{
                                            position: 'relative',
                                            borderRadius: '8px',
                                          }}
                                        >
                                          {isEditing && (
                                            <DriveFileRenameOutlineIcon
                                              sx={{
                                                position: 'absolute',
                                                top: 8,
                                                right: 8,
                                                cursor: 'pointer',
                                                height: 16,
                                                width: 16,
                                                color: 'grey.500',
                                                zIndex: 1,
                                              }}
                                              onClick={() =>
                                                handleEdit('extended', { index: index })
                                              }
                                            />
                                          )}
                                          <EnablementCard
                                            key={index}
                                            title={agent.capability_name}
                                            content={agent.description}
                                            bgcolor="#FFFBED"
                                          />
                                        </Box>
                                      ))}
                                    </Box>
                                  )}
                                </Box>
                              )}
                            </Box>
                          );
                        })()}

                        {/* Custom Section (Client Specific) - Dynamic */}
                        {(() => {
                          const customAgents = displayData?.agentData?.custom || [];

                          if (customAgents.length === 0) return null;

                          return (
                            <Box>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  mx: 2,
                                  py: 1,
                                  cursor: 'pointer',
                                  borderBottom: '1px solid #D9D9D9',
                                  mb: 2,
                                  // bgcolor: enablementExpanded['custom'] ? '#F9FAFB' : 'transparent',
                                }}
                                onClick={() => {
                                  const newExpanded = { ...enablementExpanded };
                                  newExpanded['custom'] = !newExpanded['custom'];
                                  setEnablementExpanded(newExpanded);
                                }}
                              >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Box
                                    component="img"
                                    src="/images/client_specific.svg"
                                    sx={{ width: 16, height: 16 }}
                                  />
                                  <Typography sx={{ fontWeight: 600, fontSize: '15px' }}>
                                    Custom
                                  </Typography>
                                </Box>
                                <Box
                                  sx={{
                                    transform: enablementExpanded['custom']
                                      ? 'rotate(180deg)'
                                      : 'rotate(0deg)',
                                    transition: 'transform 0.3s',
                                  }}
                                >
                                  <KeyboardArrowDownIcon />
                                </Box>
                              </Box>

                              {enablementExpanded['custom'] && (
                                <Box sx={{ px: 2, pb: 2 }}>
                                  {isIdeationEnablementLoading ? (
                                    <Box
                                      sx={{
                                        display: 'grid',
                                        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                                        gap: 2,
                                      }}
                                    >
                                      {[1].map((i) => (
                                        <Box
                                          key={i}
                                          sx={{ p: 2, borderRadius: '8px', bgcolor: '#F4FFF2' }}
                                        >
                                          <Skeleton variant="text" width="60%" height={20} />
                                          <Skeleton
                                            variant="text"
                                            width="100%"
                                            height={16}
                                            sx={{ mt: 1 }}
                                          />
                                          <Skeleton variant="text" width="100%" height={16} />
                                          <Skeleton variant="text" width="80%" height={16} />
                                        </Box>
                                      ))}
                                    </Box>
                                  ) : (
                                    <Box
                                      sx={{
                                        display: 'grid',
                                        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                                        gap: 2,
                                      }}
                                    >
                                      {customAgents.map((agent: CardData, index: number) => (
                                        <Box
                                          key={index}
                                          sx={{
                                            position: 'relative',
                                            borderRadius: '8px',
                                          }}
                                        >
                                          {isEditing && (
                                            <DriveFileRenameOutlineIcon
                                              sx={{
                                                position: 'absolute',
                                                top: 8,
                                                right: 8,
                                                cursor: 'pointer',
                                                height: 16,
                                                width: 16,
                                                color: 'grey.500',
                                                zIndex: 1,
                                              }}
                                              onClick={() => handleEdit('custom', { index: index })}
                                            />
                                          )}
                                          <EnablementCard
                                            key={index}
                                            title={agent.capability_name}
                                            content={agent.description}
                                            bgcolor="#F4FFF2"
                                          />
                                        </Box>
                                      ))}
                                    </Box>
                                  )}
                                </Box>
                              )}
                            </Box>
                          );
                        })()}
                      </CardContent>
                    </Card>

                    {/* Risks and Mitigation Section - Keep static as before */}
                    <Card sx={{ mb: 2, boxShadow: 'none' }}>
                      <CardContent
                        sx={{
                          // border: '1px solid #DDE1E6',
                          borderRadius: '10px',
                          boxShadow: 'none',
                          p: 0,
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            px: 2,
                            // borderBottom: '1px solid #DDE1E6',
                          }}
                        >
                          <Typography sx={{ fontWeight: 600, fontSize: '18px' }}>
                            Risks and Mitigation
                          </Typography>
                        </Box>
                        {displayData?.agent?.ai_enablement?.risks_and_mitigation.map(
                          (item: any, index: number) => (
                            <Box
                              sx={{ p: 1, m: 2, border: '1px solid #DDE1E6', borderRadius: '8px' }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  position: 'relative',
                                  pb: 1,
                                }}
                              >
                                <Typography
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: '16px',
                                    color: '#111827',
                                  }}
                                >
                                  {item?.risk_title || ''}
                                </Typography>

                                {isEditing && (
                                  <DriveFileRenameOutlineIcon
                                    sx={{
                                      position: 'absolute',
                                      top: 8,
                                      right: 8,
                                      cursor: 'pointer',
                                      height: 16,
                                      width: 16,
                                      color: 'grey.500',
                                      zIndex: 1,
                                    }}
                                    onClick={() => handleEdit('risk', { index: index })}
                                  />
                                )}
                              </Box>

                              <Box
                                sx={{
                                  display: 'grid',
                                  gridTemplateColumns: { xs: '1fr', md: '1fr auto 1fr' },
                                  gap: 1,
                                  alignItems: 'stretch',
                                }}
                              >
                                <EnablementCard
                                  title="Overview"
                                  content={item?.risk_overview || ''}
                                  bgcolor="#FFFFFF"
                                  padding="0px"
                                />
                                <Divider
                                  orientation="vertical"
                                  flexItem
                                  sx={{
                                    borderColor: '#DDE1E6',
                                  }}
                                />

                                <EnablementCard
                                  title="Mitigation"
                                  content={item?.mitigation || ''}
                                  bgcolor="#FFFFFF"
                                  padding="0px"
                                />
                              </Box>
                            </Box>
                          )
                        )}
                      </CardContent>
                    </Card>
                  </>
                ) : (
                  // Agents view for isIdeation = false
                  <>
                    {displayData?.llm_agent?.map((data: any, index: number) => {
                      let matched: any = undefined;
                      if (Array.isArray(displayData?.agent?.embedded)) {
                        matched = displayData?.agent?.embedded.find(
                          (item: any) => item?.agent_name === data.agent_name
                        );
                      } else if (agentLlmResponse && typeof agentLlmResponse === 'object') {
                        const candidateArray = Array.isArray(
                          (agentLlmResponse as any).agent_classification
                        )
                          ? (agentLlmResponse as any).agent_classification
                          : Object.values(agentLlmResponse as any);
                        if (Array.isArray(candidateArray)) {
                          matched = candidateArray.find(
                            (item: any) => item?.agent_name === data.agent_name
                          );
                        }
                      }

                      const styleConfig =
                        matched && IMPLEMENTATION_STYLES[matched?.implementation_type];

                      return (
                        <Grid key={index}>
                          <Card sx={{ mb: 3, boxShadow: 'none' }}>
                            <CardContent
                              sx={{
                                border: '1px solid #DDE1E6',
                                borderRadius: '10px',
                                boxShadow: 'none',
                              }}
                            >
                              {/* Header */}
                              <Box sx={{ display: 'flex', alignItems: 'start' }}>
                                <Box sx={{ display: 'flex', flex: 1, mr: 3 }}>
                                  <img src="/images/agentIcon.svg" alt="agentIcon" />
                                  <Box sx={{ ml: '12px' }}>
                                    <Typography
                                      variant="h6"
                                      sx={{ fontWeight: 600, fontSize: '12px', color: '#1B54F8' }}
                                    >
                                      {data?.agent_enabler?.join(', ')}
                                    </Typography>
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 1,
                                      }}
                                    >
                                      <Typography
                                        variant="h6"
                                        sx={{ fontWeight: 600, fontSize: '18px' }}
                                      >
                                        {data?.agent_name}
                                      </Typography>
                                      {isEditing && (
                                        <DriveFileRenameOutlineIcon
                                          sx={{
                                            cursor: 'pointer',
                                            height: '16px',
                                            width: '16px',
                                            color: 'grey.500',
                                          }}
                                          onClick={() =>
                                            handleEdit('agent_name', {
                                              index: index,
                                              name: data?.agent_name,
                                            })
                                          }
                                        />
                                      )}
                                    </Box>
                                  </Box>
                                </Box>
                              </Box>
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: { xs: 'column', sm: 'row' },
                                  gap: '15px',
                                }}
                              >
                                {renderInfoBox({
                                  title: 'Task',
                                  titleColor: '#3286DB',
                                  icon: '/images/taskIcon.svg',
                                  text: data?.agent_description,
                                  bg: '#F7FBFF',
                                  extraSx: {
                                    width:
                                      matched || isLoadingAgentLlmResponse
                                        ? { xs: '100%', sm: '50%' }
                                        : '100%',
                                  },
                                })}

                                {isLoadingAgentLlmResponse ? (
                                  <Box
                                    sx={{
                                      borderRadius: '12px',
                                      px: 2,
                                      py: 2,
                                      fontSize: '12px',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      justifyContent: 'center',
                                      background: '#FAFAFA',
                                      gap: 1.5,
                                      mt: '18px',
                                      width: { xs: '100%', sm: '50%' },
                                    }}
                                  >
                                    <Skeleton
                                      variant="rounded"
                                      width={90}
                                      height={10}
                                      animation="wave"
                                      sx={{ borderRadius: '8px' }}
                                    />
                                    <Skeleton
                                      variant="rectangular"
                                      height={10}
                                      width="100%"
                                      animation="wave"
                                      sx={{ borderRadius: '6px', mt: '10px' }}
                                    />
                                    <Skeleton
                                      variant="rectangular"
                                      height={10}
                                      width="100%"
                                      animation="wave"
                                      sx={{ borderRadius: '6px' }}
                                    />
                                    <Skeleton
                                      variant="rectangular"
                                      height={10}
                                      width="100%"
                                      animation="wave"
                                      sx={{ borderRadius: '6px' }}
                                    />
                                  </Box>
                                ) : (
                                  matched &&
                                  styleConfig &&
                                  renderInfoBox({
                                    title: matched.implementation_type,
                                    titleColor: styleConfig.color,
                                    icon: styleConfig.icon,
                                    text: matched.justification,
                                    bg: styleConfig.bg,
                                    extraSx: {
                                      width: { xs: '100%', sm: '50%' },
                                    },
                                  })
                                )}
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                      );
                    })}
                  </>
                )}
              </Box>
            )}
          </Box>
        </Grid>

        {/* <Grid size={{ xs: 12, md: 12 }}></Grid> */}

        <ConfirmationModal
          isOpen={showDeleteConfirmModal}
          title={`Delete Opportunity`}
          description={
            <Box display="flex" alignItems="center" gap={1}>
              <Typography sx={{ color: '#4f4f4f' }}>
                <Box>
                  Are you sure you want to delete this opportunity? This action cannot be undone.
                  All associated data will be permanently removed.
                </Box>
              </Typography>
            </Box>
          }
          primaryButtonText={
            <Box display="flex" alignItems="center" gap={1}>
              Delete
            </Box>
          }
          onPrimaryAction={() => {
            handleRemoveClick?.();
            setShowDeleteConfirmModal(false);
          }}
          onClose={() => setShowDeleteConfirmModal(false)}
          width={'781px'}
          outerPadding={0}
          sx={isIdeation ? { zIndex: 1500 } : undefined}
        />

        <Modal
          isOpen={showFavoriteModal}
          onClose={() => setShowFavoriteModal(false)}
          title={isFavorite ? 'Remove AI Solution from Favorites' : 'Add AI Solution to Favorites'}
          maxWidth="sm"
          height="md"
        >
          <Box sx={{ bgcolor: '#F9FBFF', borderRadius: '12px', p: 3 }}>
            <Stack spacing={2} alignItems="center">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {isFavorite ? (
                  <Star sx={{ color: '#2054F4' }} />
                ) : (
                  <StarBorder sx={{ color: '#2054F4' }} />
                )}
                <Typography sx={{ fontSize: '16px', color: '#2c2c2c' }}>
                  Are you sure you want to{' '}
                  {isFavorite ? 'remove this AI Solution from' : 'add this AI Solution to'} your
                  favorites?
                </Typography>
              </Box>
              <Button
                variant="contained"
                onClick={handleToggleFavorite}
                sx={{
                  textTransform: 'none',
                  bgcolor: '#2054F4',
                  borderRadius: '6px',
                  px: 3,
                  py: 1,
                  '&:hover': { bgcolor: '#1A45C6' },
                  fontWeight: 'bold',
                }}
                startIcon={<Add />}
              >
                {isFavorite ? 'Remove' : 'Add'}
              </Button>
            </Stack>
          </Box>
        </Modal>
        <Snackbar
          open={showApplySuccessSnackbar}
          autoHideDuration={3000}
          onClose={() => setShowApplySuccessSnackbar(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setShowApplySuccessSnackbar(false)}
            severity="success"
            sx={{ width: '100%' }}
          >
            AI Solution applied successfully
          </Alert>
        </Snackbar>
      </Grid>
      {openEditModal && (
        <SolutionEditModal
          isOpen={openEditModal}
          solutionData={updatedSolutionData}
          setSolutionData={setUpdatedSolutionData}
          onSave={handleSaveSolution}
          onClose={() => setOpenEditModal(false)}
          type={editType as EditType}
          editItem={editItem || undefined}
          setLlmResponseData={setLlmResponseData}
        />
      )}
    </>
  );
};

export default ViewSolutionDetails;
